import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuthStore, useRoomsStore, useMessagesStore, usePresenceStore } from '../store';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (element: HTMLElement, config: object) => void;
          prompt: () => void;
        };
      };
    };
  }
}



export function AuthScreen() {
  const { setAuth } = useAuthStore();
  const { resetRooms } = useRoomsStore();
  const { resetMessages } = useMessagesStore();
  const { resetPresence } = usePresenceStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  useEffect(() => {
    if (!googleClientId) return; // Skip if no client ID configured
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
      });
      const buttonEl = document.getElementById('google-signin-button');
      if (buttonEl) {
        window.google?.accounts.id.renderButton(buttonEl, {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
        });
      }
      setGoogleReady(true);
    };
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [googleClientId]);

  const handleCredentialResponse = async (response: { credential: string }) => {
    setLoading(true);
    setError(null);
    try {
      const { token, user } = await api.auth.googleLogin(response.credential);
      resetRooms();
      resetMessages();
      resetPresence();
      setAuth(user, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login Google gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-600/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo & title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-500 to-purple-600 mb-5 shadow-2xl shadow-brand-500/30">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Garut<span className="text-brand-400">Chat</span>
          </h1>
          <p className="text-dark-300 text-base">Aplikasi Real-Time Chat</p>
        </div>

        {/* Login card */}
        <div className="glass rounded-3xl p-7 shadow-2xl space-y-5">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-white mb-1">Selamat Datang</h2>
            <p className="text-dark-400 text-sm">Masuk untuk mulai chat</p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: '💬', label: 'Personal & Group' },
              { icon: '📸', label: 'Kirim Foto' },
              { icon: '📍', label: 'Share Lokasi' },
            ].map((f) => (
              <div key={f.label} className="glass rounded-xl p-2.5 text-center">
                <div className="text-xl mb-1">{f.icon}</div>
                <div className="text-[11px] text-dark-300 leading-tight">{f.label}</div>
              </div>
            ))}
          </div>

          {/* Google Sign-In */}
          <div className="relative pt-2">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-dark-800/80 rounded-xl z-10">
                <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {googleClientId ? (
              <>
                <div id="google-signin-button" className="w-full flex justify-center min-h-[44px]" />
                {!googleReady && <div className="w-full h-11 shimmer rounded-xl" />}
              </>
            ) : (
              <div className="w-full h-11 flex items-center justify-center rounded-xl border border-dark-600 text-dark-500 text-sm gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Tambahkan VITE_GOOGLE_CLIENT_ID untuk login Google
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm text-center animate-fade-in">
              ⚠️ {error}
            </div>
          )}

          <p className="text-center text-xs text-dark-600">
            Dengan masuk, Anda menyetujui syarat penggunaan kami.
          </p>
        </div>
      </div>
    </div>
  );
}
